import { Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useDrop } from "react-dnd";
import { useSelector } from "react-redux";
import type { ConditionConfiguration } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { StatementDataColumnConfiguration } from "../../../../../../../../api/types/svTypes.ts";
import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import {
  selectFinancialStatementSettings,
  selectStatementAxes,
  selectStatementDataColumns,
  selectStatementLines,
} from "../../../../store/selectors/fieldsStateSelectors.ts";
import { selectFieldCaptionLookup } from "../../../../store/selectors/metaDataSelectors.ts";
import { createFinancialStatementAction } from "../../../../store/utils/storeUtils.ts";
import AddField from "../../../viewer/common/fields/area-fields/AddField.tsx";
import { hasSingleDataColumn, isDataColumn } from "../../../viewer/financialStatement/statementColumnLabels.ts";
import {
  axesAreaItemTypes,
  createDataColumn,
  createFormulaColumn,
  getColumnCaption,
  getDataColumnHint,
  getFormulaReferableColumns,
  getRowsPeriod,
  resolveDataColumnsDrop,
} from "../utils/statementColumns.ts";
import { getRemoveBlockedMessage } from "../utils/statementFormulas.ts";
import type { StatementFieldDragItem } from "../utils/statementRowsArea.ts";
import DataColumnCard from "./DataColumnCard.tsx";
import DataColumnOptionsPopover from "./DataColumnOptionsPopover.tsx";
import FormulaColumnDialog from "./FormulaColumnDialog.tsx";
import useAxisOutcomeHandler from "./useAxisOutcomeHandler.ts";

interface ColumnOptionsTarget {
  guid: string;
  anchorEl: HTMLElement;
}

interface FormulaColumnTarget {
  column: StatementDataColumnConfiguration;
  isNew: boolean;
}

interface Props {
  readonlyMode: boolean;
}

const DataColumnsArea = ({ readonlyMode }: Props) => {
  const dispatch = useAppDispatch();
  const { conditions } = useBiCommonDataContext();
  const dataColumns = useSelector(selectStatementDataColumns);
  const axes = useSelector(selectStatementAxes);
  const lines = useSelector(selectStatementLines);
  const settings = useSelector(selectFinancialStatementSettings);
  const getFieldCaption = useSelector(selectFieldCaptionLookup);
  const [optionsTarget, setOptionsTarget] = useState<ColumnOptionsTarget>();
  const [formulaTarget, setFormulaTarget] = useState<FormulaColumnTarget>();
  const handleAxisOutcome = useAxisOutcomeHandler();

  const handleDrop = useCallback(
    (item: StatementFieldDragItem) => handleAxisOutcome(resolveDataColumnsDrop(item, { axes, lines, dataColumns })),
    [axes, dataColumns, handleAxisOutcome, lines]
  );

  const handleShowOptions = useCallback(
    (guid: string, anchorEl: HTMLElement) => setOptionsTarget({ guid, anchorEl }),
    []
  );

  const handleRemove = useCallback(
    (guid: string) => dispatch(createFinancialStatementAction("removeDataColumn", guid)),
    [dispatch]
  );

  const [{ isOver }, drop] = useDrop<StatementFieldDragItem, void, { isOver: boolean }>(
    () => ({
      accept: axesAreaItemTypes,
      canDrop: () => !readonlyMode,
      drop: handleDrop,
      collect: (monitor) => ({ isOver: monitor.isOver() && monitor.canDrop() }),
    }),
    [handleDrop, readonlyMode]
  );
  const handleAreaRef = useCallback(
    (element: HTMLDivElement | null) => {
      drop(element);
    },
    [drop]
  );

  const handleEditCalculation = (column: StatementDataColumnConfiguration) => {
    setOptionsTarget(undefined);
    setFormulaTarget({ column, isNew: false });
  };

  const handleRemoveFromOptions = (guid: string) => {
    setOptionsTarget(undefined);
    handleRemove(guid);
  };

  const isSingleDataColumn = hasSingleDataColumn(dataColumns);
  const isOnlyDataColumn = (column: StatementDataColumnConfiguration): boolean =>
    isSingleDataColumn && isDataColumn(column);
  const reportConditions = conditions.map((condition) => condition.config);
  const getLabelConditions = (column: StatementDataColumnConfiguration): ConditionConfiguration[] => [
    ...column.customConditions,
    ...reportConditions,
  ];
  const getCardLabel = (column: StatementDataColumnConfiguration): string =>
    getColumnCaption(column, getLabelConditions(column), getFieldCaption);
  const getRemoveBlocked = (column: StatementDataColumnConfiguration): string | undefined =>
    getRemoveBlockedMessage(dataColumns, column.guid, getCardLabel, "formula column");
  const rowsPeriod = getRowsPeriod(lines, settings.defaultDateRange);
  const optionsColumn = optionsTarget && dataColumns.find((column) => column.guid === optionsTarget.guid);

  return (
    <Stack sx={{ gap: ".5rem" }}>
      <Typography variant="subtitle2" color="textPrimary">
        Data Columns
      </Typography>
      <Stack
        ref={handleAreaRef}
        sx={(theme) => ({
          gap: "5px",
          minHeight: readonlyMode ? "40px" : "90px",
          p: ".5rem",
          bgcolor: "background.grey",
          border: `1px solid ${isOver ? theme.palette.primary.main : theme.palette.divider}`,
          borderRadius: "4px",
        })}
      >
        {dataColumns.map((column) => (
          <DataColumnCard
            key={column.guid}
            column={column}
            label={getCardLabel(column)}
            hint={getDataColumnHint(column, rowsPeriod)}
            isOnlyDataColumn={isOnlyDataColumn(column)}
            removeBlockedMessage={getRemoveBlocked(column)}
            optionsOpen={column.guid === optionsTarget?.guid}
            readonly={readonlyMode}
            onShowOptions={handleShowOptions}
            onRemove={handleRemove}
          />
        ))}
        {!readonlyMode && (
          <>
            <AddField
              disabled={false}
              onAddField={() =>
                dispatch(createFinancialStatementAction("addDataColumn", createDataColumn(dataColumns)))
              }
            >
              Add data column...
            </AddField>
            <AddField
              disabled={false}
              onAddField={() => setFormulaTarget({ column: createFormulaColumn(dataColumns), isNew: true })}
            >
              Add formula column...
            </AddField>
          </>
        )}
      </Stack>
      {optionsTarget && optionsColumn && (
        <DataColumnOptionsPopover
          column={optionsColumn}
          isOnlyDataColumn={isOnlyDataColumn(optionsColumn)}
          labelConditions={getLabelConditions(optionsColumn)}
          rowsPeriod={rowsPeriod}
          lines={lines}
          getFieldCaption={getFieldCaption}
          removeBlockedMessage={getRemoveBlocked(optionsColumn)}
          anchorEl={optionsTarget.anchorEl}
          readonly={readonlyMode}
          onEditCalculation={() => handleEditCalculation(optionsColumn)}
          onRemove={() => handleRemoveFromOptions(optionsColumn.guid)}
          onClose={() => setOptionsTarget(undefined)}
        />
      )}
      {formulaTarget && (
        <FormulaColumnDialog
          column={formulaTarget.column}
          isNew={formulaTarget.isNew}
          referableFields={getFormulaReferableColumns(dataColumns, getCardLabel)}
          onClose={() => setFormulaTarget(undefined)}
        />
      )}
    </Stack>
  );
};

export default DataColumnsArea;
